/**
 * Sidebar Tags Section
 *
 * Manages the tag definitions list in the sidebar, grouped by category
 * (engine/core/tag-filter.js). Allows creating (in Labels), renaming, deleting tags
 * (non-mandatory), and toggling hide/highlight per tag, per category's
 * "Not tagged" entry, and in bulk.
 */

import { groupTags, DEFAULT_TAG_CATEGORY_ID, tagLabel } from '../../../engine/core/tag-filter.js';
import { t } from '../../../shared/i18n/index.js';

const EYE_ICON = '<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></svg>';
const EYE_OFF_ICON = '<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M17.94 17.94A10.07 10.07 0 0112 20c-7 0-11-8-11-8a18.45 18.45 0 015.06-5.94"/><path d="M9.9 4.24A9.12 9.12 0 0112 4c7 0 11 8 11 8a18.5 18.5 0 01-2.16 3.19"/><line x1="1" y1="1" x2="23" y2="23"/></svg>';
const STAR_POINTS = '12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2';
const STAR_ICON = `<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><polygon points="${STAR_POINTS}"/></svg>`;
const STAR_FILLED_ICON = `<svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor" stroke="currentColor" stroke-width="2"><polygon points="${STAR_POINTS}"/></svg>`;

let callbacks = null;
let listEl = null;
let addBtn = null;
let sliderContainerEl = null;

/**
 * Initialize the sidebar tags section.
 * @param {Object} cbs - Callbacks
 */
export function init(cbs) {
  callbacks = cbs;
  listEl = document.getElementById('sidebar-tags-list');
  addBtn = document.getElementById('sidebar-tag-add');
  sliderContainerEl = document.getElementById('sidebar-tags-slider');

  if (addBtn) {
    addBtn.addEventListener('click', handleAddTag);
  }

  // Apply initial dim level CSS variable
  applyDimLevel(cbs.getTagDimLevel?.() ?? 80);
}

export function render() {
  if (!listEl || !callbacks) return;

  const defs = callbacks.getTagDefinitions();
  const groups = groupTags(defs, callbacks.getTagCategories?.() || []);
  const hiddenIds = callbacks.getHiddenTagIds();
  const highlightedIds = callbacks.getHighlightedTagIds();

  listEl.innerHTML = '';

  // One block per category: header (name, show / hide all, highlight all /
  // none), its tags, then its synthetic "Not tagged" entry.
  for (const group of groups) {
    listEl.appendChild(groupHeader(group, hiddenIds, highlightedIds));
    for (const tag of group.tags) {
      listEl.appendChild(tagRow(tag, hiddenIds, highlightedIds));
    }
    listEl.appendChild(notTaggedRow(group, hiddenIds, highlightedIds));
  }

  // Dim level slider — rendered into separate container (below add button)
  // Preserve existing slider to avoid destroying during drag
  if (sliderContainerEl && !sliderContainerEl.querySelector('.sidebar-tag-slider')) {
    const dimLevel = callbacks.getTagDimLevel?.() ?? 80;
    const sliderRow = document.createElement('div');
    sliderRow.className = 'sidebar-tag-slider-row';

    const label = document.createElement('span');
    label.className = 'sidebar-tag-slider-label';
    label.textContent = t('app.sidebarTags.highlight');
    sliderRow.appendChild(label);

    const slider = document.createElement('input');
    slider.type = 'range';
    slider.min = '0';
    slider.max = '90';
    slider.step = '10';
    slider.value = Math.min(dimLevel, 90);
    slider.className = 'sidebar-tag-slider';
    slider.addEventListener('input', () => {
      applyDimLevel(parseInt(slider.value, 10));
    });
    slider.addEventListener('change', () => {
      callbacks.setTagDimLevel?.(parseInt(slider.value, 10));
    });
    sliderRow.appendChild(slider);

    sliderContainerEl.appendChild(sliderRow);
  }
}

const groupFilterIds = (group) => [...group.tags.map((tag) => tag.id), group.notTaggedId];

const without = (list, ids) => list.filter((id) => !ids.includes(id));
const withAll = (list, ids) => [...new Set([...list, ...ids])];

// Last bulk action per category and button ("all" | "none"), for this session.
const lastBulkAction = new Map();

/**
 * One button that sets every entry of a category on ("all") or off ("none").
 * Each click does the opposite of the previous one; the first click (or a
 * click that would change nothing) goes to whichever state isn't current.
 * The icon shows the category's current state: all, none, or mixed (dimmed).
 */
function superButton(group, kind, { isOn, allOnIcon, noneIcon, onClass, labels, set }) {
  const ids = groupFilterIds(group);
  const onCount = ids.filter(isOn).length;
  const allOn = onCount === ids.length;
  const noneOn = onCount === 0;
  const key = `${group.id}:${kind}`;
  let next = lastBulkAction.get(key) === 'all' ? 'none' : lastBulkAction.get(key) === 'none' ? 'all' : allOn ? 'none' : 'all';
  if (next === 'all' && allOn) next = 'none';
  if (next === 'none' && noneOn) next = 'all';

  const btn = document.createElement('button');
  btn.type = 'button';
  btn.className = `sidebar-tag-btn sidebar-tag-super ${kind}`
    + (noneOn ? '' : ` ${onClass}`) + (!allOn && !noneOn ? ' mixed' : '');
  btn.innerHTML = noneOn ? noneIcon : allOnIcon;
  btn.title = next === 'all' ? labels.all : labels.none;
  btn.setAttribute('aria-label', btn.title);
  btn.addEventListener('click', (e) => {
    e.stopPropagation();
    lastBulkAction.set(key, next);
    set(ids, next);
    render();
  });
  return btn;
}

/** Category header: its name, then one eye and one star for the whole
 *  category, in the same columns as the tag rows' eye and star. */
function groupHeader(group, hiddenIds, highlightedIds) {
  const header = document.createElement('div');
  header.className = 'sidebar-tag-group-header';
  header.dataset.categoryId = group.id;
  const name = document.createElement('span');
  name.className = 'sidebar-tag-group-name';
  name.textContent = group.name;
  header.append(
    name,
    superButton(group, 'eye', {
      isOn: (id) => !hiddenIds.includes(id),
      allOnIcon: EYE_ICON,
      noneIcon: EYE_OFF_ICON,
      onClass: 'visible',
      labels: { all: t('app.sidebarTags.showAll', { category: group.name }), none: t('app.sidebarTags.hideAll', { category: group.name }) },
      set: (ids, next) => callbacks.setHiddenTagIds(
        next === 'all' ? without(callbacks.getHiddenTagIds(), ids) : withAll(callbacks.getHiddenTagIds(), ids)),
    }),
    superButton(group, 'star', {
      isOn: (id) => highlightedIds.includes(id),
      allOnIcon: STAR_FILLED_ICON,
      noneIcon: STAR_ICON,
      onClass: 'active',
      labels: { all: t('app.sidebarTags.highlightAll', { category: group.name }), none: t('app.sidebarTags.clearHighlights', { category: group.name }) },
      set: (ids, next) => callbacks.setHighlightedTagIds(
        next === 'all' ? withAll(callbacks.getHighlightedTagIds(), ids) : without(callbacks.getHighlightedTagIds(), ids)),
    }),
    spacer(), // delete-button column
  );
  return header;
}

/** labels: { show, hide, highlight } — translated sentences for this entry. */
function eyeButton(filterId, isHidden, labels) {
  const eye = document.createElement('button');
  eye.className = 'sidebar-tag-btn' + (isHidden ? '' : ' visible');
  eye.innerHTML = isHidden ? EYE_OFF_ICON : EYE_ICON;
  eye.title = isHidden ? labels.show : labels.hide;
  eye.addEventListener('click', (e) => {
    e.stopPropagation();
    toggleHidden(filterId);
  });
  return eye;
}

function starButton(filterId, isHighlighted, labels) {
  const hl = document.createElement('button');
  hl.className = 'sidebar-tag-btn' + (isHighlighted ? ' active' : '');
  hl.innerHTML = STAR_ICON;
  hl.title = isHighlighted ? t('app.sidebarTags.removeHighlight') : labels.highlight;
  hl.addEventListener('click', (e) => {
    e.stopPropagation();
    toggleHighlighted(filterId);
  });
  return hl;
}

function spacer() {
  const el = document.createElement('span');
  el.className = 'sidebar-tag-btn-spacer';
  return el;
}

function tagRow(tag, hiddenIds, highlightedIds) {
  const row = document.createElement('div');
  row.className = 'sidebar-tag-row';
  row.dataset.tagId = tag.id;

  // Name (double-click to rename)
  const name = document.createElement('span');
  name.className = 'sidebar-tag-name';
  name.textContent = tagLabel(tag);
  if (tag.mandatory) {
    name.classList.add('mandatory');
  } else {
    name.title = t('app.sidebarTags.renameHint');
    name.addEventListener('dblclick', (e) => {
      e.stopPropagation();
      startRename(tag, name);
    });
  }
  row.appendChild(name);
  const labels = {
    show: t('app.sidebarTags.showTagged'),
    hide: t('app.sidebarTags.hideTagged'),
    highlight: t('app.sidebarTags.highlightTagged'),
  };
  row.appendChild(eyeButton(tag.id, hiddenIds.includes(tag.id), labels));
  row.appendChild(starButton(tag.id, highlightedIds.includes(tag.id), labels));

  if (!tag.mandatory) {
    const del = document.createElement('button');
    del.className = 'sidebar-tag-btn sidebar-tag-btn-delete';
    del.innerHTML = '\u00D7';
    del.title = t('app.sidebarTags.deleteTag');
    del.addEventListener('click', (e) => {
      e.stopPropagation();
      confirmDeleteTag(tag, hiddenIds, highlightedIds);
    });
    row.appendChild(del);
  } else {
    row.appendChild(spacer()); // keeps eye/star aligned with deletable rows
  }
  return row;
}

/** Synthetic entry for nodes carrying none of the category's tags (no rename,
 *  no delete, not offered in the tag popup). */
function notTaggedRow(group, hiddenIds, highlightedIds) {
  const id = group.notTaggedId;
  const row = document.createElement('div');
  row.className = 'sidebar-tag-row sidebar-tag-row-synthetic';
  row.dataset.tagId = id;
  const name = document.createElement('span');
  name.className = 'sidebar-tag-name synthetic';
  name.textContent = t('app.sidebarTags.notTagged');
  name.title = t('app.sidebarTags.notTaggedHint', { category: group.name });
  row.appendChild(name);
  const category = group.name;
  const labels = {
    show: t('app.sidebarTags.showUntagged', { category }),
    hide: t('app.sidebarTags.hideUntagged', { category }),
    highlight: t('app.sidebarTags.highlightUntagged', { category }),
  };
  row.appendChild(eyeButton(id, hiddenIds.includes(id), labels));
  row.appendChild(starButton(id, highlightedIds.includes(id), labels));
  row.appendChild(spacer());
  return row;
}

function applyDimLevel(level) {
  document.documentElement.style.setProperty('--tag-dim-amount', level / 100);
}

function confirmDeleteTag(tag, hiddenIds, highlightedIds) {
  // Remove any existing confirmation modal
  document.querySelector('.sidebar-tag-confirm-delete')?.remove();

  const overlay = document.createElement('div');
  overlay.className = 'sidebar-tag-confirm-delete';

  const modal = document.createElement('div');
  modal.className = 'sidebar-tag-confirm-modal';
  modal.innerHTML = `
    <div class="sidebar-tag-confirm-title"></div>
    <div class="sidebar-tag-confirm-text"></div>
    <div class="sidebar-tag-confirm-btns">
      <button class="sidebar-tag-confirm-btn cancel"></button>
      <button class="sidebar-tag-confirm-btn delete"></button>
    </div>
  `;
  modal.querySelector('.sidebar-tag-confirm-text').textContent = t('app.sidebarTags.deleteText');
  modal.querySelector('.cancel').textContent = t('common.cancel');
  modal.querySelector('.delete').textContent = t('common.delete');
  // Tag names are user/collaborator content — never interpolate into HTML
  modal.querySelector('.sidebar-tag-confirm-title').textContent = t('app.sidebarTags.deleteTitle', { name: tag.name });

  overlay.appendChild(modal);
  document.body.appendChild(overlay);

  modal.querySelector('.cancel').addEventListener('click', () => overlay.remove());
  overlay.addEventListener('click', (e) => { if (e.target === overlay) overlay.remove(); });

  modal.querySelector('.delete').addEventListener('click', () => {
    callbacks.deleteTagDefinition(tag.id);
    if (hiddenIds.includes(tag.id)) {
      callbacks.setHiddenTagIds(hiddenIds.filter(id => id !== tag.id));
    }
    if (highlightedIds.includes(tag.id)) {
      callbacks.setHighlightedTagIds(highlightedIds.filter(id => id !== tag.id));
    }
    overlay.remove();
    render();
  });
}

function handleAddTag() {
  if (!callbacks) return;
  const defs = callbacks.getTagDefinitions();
  const tag = {
    id: callbacks.generateId(),
    name: t('app.sidebarTags.newTagName', { n: defs.length + 1 }),
    categoryId: DEFAULT_TAG_CATEGORY_ID,
  };
  callbacks.addTagDefinition(tag);
  render();

  requestAnimationFrame(() => {
    const nameEl = listEl.querySelector(`[data-tag-id="${tag.id}"] .sidebar-tag-name`);
    if (nameEl) startRename(tag, nameEl);
  });
}

function startRename(tag, nameEl) {
  const input = document.createElement('input');
  input.type = 'text';
  input.className = 'sidebar-tag-rename-input';
  input.value = tag.name;
  nameEl.replaceWith(input);
  input.focus();
  input.select();

  const commit = () => {
    const newName = input.value.trim() || tag.name;
    callbacks.updateTagDefinition(tag.id, { name: newName });
    render();
  };

  input.addEventListener('blur', commit);
  input.addEventListener('keydown', (e) => {
    if (e.key === 'Enter') { e.preventDefault(); input.blur(); }
    if (e.key === 'Escape') { e.preventDefault(); input.value = tag.name; input.blur(); }
  });
}

function toggleHidden(tagId) {
  const ids = callbacks.getHiddenTagIds();
  const has = ids.includes(tagId);
  callbacks.setHiddenTagIds(has ? ids.filter(id => id !== tagId) : [...ids, tagId]);
  render();
}

function toggleHighlighted(tagId) {
  const ids = callbacks.getHighlightedTagIds();
  const has = ids.includes(tagId);
  callbacks.setHighlightedTagIds(has ? ids.filter(id => id !== tagId) : [...ids, tagId]);
  render();
}
